import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { PagedData, PaginationMeta } from './common';

export type ConventionFile = {
  fileName: string;
  content: string;
};

export type ConventionMember = {
  id: string;
  nickname: string;
  email: string;
};

export type Convention = {
  id: string;
  projectId: string;
  title: string;
  category: string;
  content: string;
  contentTokenCount?: number | null;
  contentPlainText: string;
  memberId: string;
  member: ConventionMember;
  fileName: string | null;
  trigger: string | null;
  description: string | null;
  agentInstruction: string | null;
  scope?: string;
  tags?: string[];
  runnerInjectedCount: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  archivedAt: string | null;
  archivedByMemberId: string | null;
};

export type ConventionArchivedFilter = 'ACTIVE' | 'ARCHIVED' | 'ALL';

export type ConventionListItem = {
  id: string;
  projectId: string;
  title: string;
  category: string;
  contentTokenCount?: number | null;
  memberId: string;
  member: ConventionMember;
  fileName: string | null;
  trigger: string | null;
  description: string | null;
  agentInstruction: string | null;
  scope?: string;
  tags?: string[];
  runnerInjectedCount: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  archivedAt: string | null;
  archivedByMemberId: string | null;
};

export type ConventionHistory = {
  id: string;
  conventionId: string;
  title: string;
  category: string;
  fileName: string | null;
  trigger: string | null;
  description: string | null;
  agentInstruction: string | null;
  memberId: string;
  member: ConventionMember;
  createdAt: string;
};

export type ConventionHistoryDetail = ConventionHistory & {
  content?: string;
  contentMarkdown?: string;
};

export type ConventionUsageSummaryRow = {
  conventionId: string;
  title: string;
  fileName: string | null;
  totalRef: number;
  completedRef: number;
  partialRef: number;
  failedRef: number;
  avgQualityScore: number | null;
  successRate: number | null;
  planRef: number;
  planOnlyRef: number;
  reportOnlyRef: number;
  bothRef: number;
};

export type ConventionUsageSummary = {
  data: ConventionUsageSummaryRow[];
  period: { from: string; to: string };
};

type ConventionResponse = {
  data: ConventionFile;
};

type ConventionListResponse = {
  data: ConventionListItem[];
};

type ConventionPagedResponse = {
  data: ConventionListItem[];
  meta: PaginationMeta;
};

type ConventionDetailResponse = {
  data: Convention;
};

type ConventionHistoryResponse = {
  data: ConventionHistory[];
};

type ConventionHistoryDetailResponse = {
  data: ConventionHistoryDetail;
};

export const fetchConvention = async (token: string, projectId: string, id: string): Promise<ConventionFile> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/agent-configs/${id}/convention`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch convention: ${response.status}`);
  }

  const data = (await response.json()) as ConventionResponse;
  return data.data;
};

export const fetchConventions = async (
  token: string,
  projectId: string,
  params?: {
    category?: string;
    scope?: string;
    archived?: ConventionArchivedFilter;
    search?: string;
    createdByMemberId?: string;
    page?: number;
    pageSize?: number;
  },
): Promise<ConventionListItem[]> => {
  const queryParams = new URLSearchParams();
  if (params?.category) queryParams.set('category', params.category);
  if (params?.scope) queryParams.set('scope', params.scope);
  if (params?.archived && params.archived !== 'ACTIVE') queryParams.set('archived', params.archived);
  if (params?.search) queryParams.set('search', params.search);
  if (params?.createdByMemberId) queryParams.set('createdByMemberId', params.createdByMemberId);
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));

  const url = `${getApiUrl()}/api/projects/${projectId}/conventions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch conventions: ${response.status}`);
  }

  const data = (await response.json()) as ConventionListResponse;
  return data.data;
};

export const fetchConventionsPaged = async (
  token: string,
  projectId: string,
  params?: {
    category?: string;
    scope?: string;
    archived?: ConventionArchivedFilter;
    search?: string;
    createdByMemberId?: string;
    page?: number;
    pageSize?: number;
  },
): Promise<PagedData<ConventionListItem>> => {
  const queryParams = new URLSearchParams();
  if (params?.category) queryParams.set('category', params.category);
  if (params?.scope) queryParams.set('scope', params.scope);
  if (params?.archived && params.archived !== 'ACTIVE') queryParams.set('archived', params.archived);
  if (params?.search) queryParams.set('search', params.search);
  if (params?.createdByMemberId) queryParams.set('createdByMemberId', params.createdByMemberId);
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));

  const url = `${getApiUrl()}/api/projects/${projectId}/conventions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch conventions: ${response.status}`);
  }

  const data = (await response.json()) as ConventionPagedResponse;
  return data;
};

export const fetchConventionUsageSummary = async (
  token: string,
  projectId: string,
  options?: { from?: string; to?: string },
): Promise<ConventionUsageSummary> => {
  const queryParams = new URLSearchParams();
  if (options?.from) queryParams.set('from', options.from);
  if (options?.to) queryParams.set('to', options.to);

  const url = `${getApiUrl()}/api/projects/${projectId}/conventions/usage-summary${
    queryParams.toString() ? `?${queryParams.toString()}` : ''
  }`;
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch convention usage summary: ${response.status}`);
  }

  return (await response.json()) as ConventionUsageSummary;
};

export const fetchConventionById = async (
  token: string,
  projectId: string,
  conventionId: string,
): Promise<Convention> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/conventions/${conventionId}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch convention: ${response.status}`);
  }

  const data = (await response.json()) as ConventionDetailResponse;
  return data.data;
};

export const createConvention = async (
  token: string,
  projectId: string,
  input: {
    title: string;
    category: string;
    content: string;
    fileName?: string | null;
    trigger?: string | null;
    description?: string | null;
    agentInstruction?: string | null;
    scope?: string;
    tags?: string[];
  },
): Promise<Convention> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/conventions`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create convention: ${response.status}`);
  }

  const data = (await response.json()) as ConventionDetailResponse;
  return data.data;
};

export const updateConvention = async (
  token: string,
  projectId: string,
  conventionId: string,
  input: {
    updatedAt: string;
    title?: string;
    category?: string;
    content?: string;
    fileName?: string | null;
    trigger?: string | null;
    description?: string | null;
    agentInstruction?: string | null;
    scope?: string;
    tags?: string[];
  },
): Promise<Convention> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/conventions/${conventionId}`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update convention: ${response.status}`);
  }

  const data = (await response.json()) as ConventionDetailResponse;
  return data.data;
};

export const restoreConventionHistory = async (
  token: string,
  projectId: string,
  conventionId: string,
  historyId: string,
): Promise<Convention> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/conventions/${conventionId}/history/${historyId}/restore`,
    {
      method: 'POST',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to restore convention: ${response.status}`);
  }

  const data = (await response.json()) as ConventionDetailResponse;
  return data.data;
};

export const deleteConvention = async (token: string, projectId: string, conventionId: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/conventions/${conventionId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete convention: ${response.status}`);
  }
};

export const archiveConvention = async (
  token: string,
  projectId: string,
  conventionId: string,
): Promise<Convention> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/conventions/${conventionId}/archive`,
    {
      method: 'POST',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to archive convention: ${response.status}`);
  }

  const data = (await response.json()) as ConventionDetailResponse;
  return data.data;
};

export const unarchiveConvention = async (
  token: string,
  projectId: string,
  conventionId: string,
): Promise<Convention> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/conventions/${conventionId}/unarchive`,
    {
      method: 'POST',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to unarchive convention: ${response.status}`);
  }

  const data = (await response.json()) as ConventionDetailResponse;
  return data.data;
};

export const downloadConventionMarkdown = async (
  token: string,
  projectId: string,
  conventionId: string,
): Promise<string> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/conventions/${conventionId}/download`,
    {
      cache: 'no-store',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to download convention markdown: ${response.status}`);
  }

  return response.text();
};

export const fetchConventionHistory = async (
  token: string,
  projectId: string,
  conventionId: string,
): Promise<ConventionHistory[]> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/conventions/${conventionId}/history`,
    {
      cache: 'no-store',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch convention history: ${response.status}`);
  }

  const data = (await response.json()) as ConventionHistoryResponse;
  return data.data;
};

export const fetchConventionHistoryById = async (
  token: string,
  projectId: string,
  conventionId: string,
  historyId: string,
): Promise<ConventionHistoryDetail> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/conventions/${conventionId}/history/${historyId}`,
    {
      cache: 'no-store',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch convention history detail: ${response.status}`);
  }

  const data = (await response.json()) as ConventionHistoryDetailResponse;
  return data.data;
};
